import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Heart,
  Plus,
  Gift,
  Headphones,
  Receipt
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { SEOHead } from '@/components/seo/seo-head';

const ICON_COLOR_CLASSES: Record<string, { bg: string; text: string; activeBg: string }> = {
  lavender: { bg: 'bg-lavender-50', text: 'text-lavender-500', activeBg: 'bg-lavender-400/20' },
  terracotta: { bg: 'bg-terracotta-50', text: 'text-terracotta-500', activeBg: 'bg-terracotta-400/20' },
  sage: { bg: 'bg-sage-50', text: 'text-sage-500', activeBg: 'bg-sage-400/20' },
  'dusty-blue': { bg: 'bg-dusty-blue-50', text: 'text-dusty-blue-500', activeBg: 'bg-dusty-blue-400/20' },
};

const navItems = [
  { href: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'lavender' },
  { href: '/app/events', label: 'Evenimente', icon: Calendar, color: 'terracotta' },
  { href: '/app/subscriptions', label: 'Abonamente', icon: CreditCard, color: 'sage' },
  { href: '/app/settings', label: 'Setări', icon: Settings, color: 'dusty-blue' },
  { href: '/app/invoices', label: 'Facturi', icon: Receipt, color: 'sage' },
  { href: '/app/gifts', label: 'Cadouri', icon: Gift, color: 'terracotta' },
  { href: '/app/support', label: 'Support', icon: Headphones, color: 'lavender' },
];

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isEditorRoute = location.pathname.endsWith('/editor');

  const currentNavItem = navItems.find((item) => 
    location.pathname === item.href || location.pathname.startsWith(item.href + '/')
  );

  return (
    <div className="min-h-screen flex section-warm relative">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-foreground focus:outline-none">Salt la conținut</a>
      <SEOHead title="Dashboard" noIndex />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        id="sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-500 ease-in-out lg:translate-x-0 lg:static border-r border-border/30',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{
          background: 'linear-gradient(180deg, hsl(40 40% 99%) 0%, hsl(40 30% 97%) 40%, hsl(262 15% 97%) 100%)',
        }}
      >
        <div className="h-full flex flex-col relative overflow-hidden">
          {/* Subtle decorative orb */}
          <div
            className="absolute -top-20 -right-20 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, hsl(16 50% 85% / 0.2) 0%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-20 -left-10 w-32 h-32 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, hsl(262 40% 88% / 0.15) 0%, transparent 70%)' }}
          />

          {/* Logo */}
          <div className="h-20 flex items-center justify-between px-8 border-b border-border/30 relative z-10">
            <a href="/" className="flex items-center gap-2.5 text-xl font-serif font-bold tracking-tight">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-terracotta-400 to-terracotta-500 flex items-center justify-center shadow-lg shadow-terracotta-500/20">
                <Heart className="w-4 h-4 text-white fill-white/20" />
              </div>
              <span className="text-gradient-warm">Invitații Online</span>
            </a>
            <button
              className="lg:hidden p-2 rounded-xl hover:bg-white/60 transition-colors"
              onClick={() => setSidebarOpen(false)}
              aria-label="Închide meniul"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Quick action */}
          <div className="px-6 py-8 relative z-10">
            <Button
              variant="foreground"
              className="w-full justify-center gap-2.5 h-12 rounded-2xl shadow-lg shadow-foreground/10 hover:shadow-xl transition-all duration-300 group"
              asChild
            >
              <Link to="/app/events/new">
                <Plus className="w-5 h-5 transition-transform group-hover:rotate-90" />
                <span className="font-medium">Creează Eveniment</span>
              </Link>
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto scrollbar-thin relative z-10">
            <p className="px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 mb-3 mt-2">Meniu Principal</p>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href ||
                              (item.href !== '/app/dashboard' && location.pathname.startsWith(item.href));
              const iconColors = ICON_COLOR_CLASSES[item.color] || ICON_COLOR_CLASSES.lavender;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3.5 px-4 py-3 rounded-2xl transition-all duration-300 group relative overflow-hidden',
                    isActive
                      ? 'nav-item-active text-white'
                      : 'hover:bg-white/70 text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className={cn(
                    'w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0',
                    isActive
                      ? iconColors.activeBg
                      : cn(iconColors.bg, 'group-hover:scale-105'),
                  )}>
                    <Icon className={cn('h-4 w-4 transition-colors duration-300',
                      isActive ? 'text-white' : iconColors.text
                    )} />
                  </div>
                  <span className="font-medium text-sm tracking-tight">{item.label}</span>
                  {isActive && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-terracotta-400 rounded-l-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-6 mt-auto relative z-10">
            <div className="p-4 rounded-3xl bg-white/60 backdrop-blur-sm border border-white/40 mb-4 group transition-all duration-300 hover:bg-white hover:shadow-premium hover:border-border/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-terracotta-100 to-lavender-100 flex items-center justify-center border border-white/60 shadow-inner group-hover:scale-105 transition-transform duration-500">
                  <span className="text-sm font-bold text-foreground/70 tracking-tighter">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate tracking-tight">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate font-medium uppercase tracking-wider">{user?.role || 'Utilizator'}</p>
                </div>
              </div>
            </div>

            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-12 rounded-2xl text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
              <span className="font-medium text-sm tracking-tight">Deconectare</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-white/60 backdrop-blur-xl border-b border-border/30 flex items-center justify-between px-6 lg:px-12 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2.5 -ml-2 rounded-2xl hover:bg-secondary transition-colors"
              onClick={() => setSidebarOpen(true)}
              aria-label="Deschide meniul"
              aria-expanded={sidebarOpen}
              aria-controls="sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60 leading-none mb-1">Pagina</span>
              <h2 className="text-lg font-serif font-bold tracking-tight text-foreground leading-none">
                {currentNavItem?.label || 'Dashboard'}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <NotificationsBell />
          </div>
        </header>

        <main
          id="main"
          className={cn(
            'flex-1 overflow-y-auto scrollbar-hide',
            // The invitation editor is a full-bleed experience (own sticky header +
            // internal max-width). On mobile, the dashboard's own padding made it look
            // boxed-in and not full width — drop the padding there for the editor route.
            isEditorRoute ? 'p-0 lg:p-12' : 'p-6 lg:p-12'
          )}
        >
          <div className={isEditorRoute ? 'lg:max-w-6xl lg:mx-auto' : 'max-w-6xl mx-auto'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
